import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';

import BasicText, { IBasicTextProps } from '@ui/atoms/BasicText';

import spaced from '@ui/styled/Spaced';

import { exists } from '@utils/general';

import { SpacingTypes } from '@folx-types/theme';

import theme from '@theme/FolxTheme';

export interface ISelectableGroupProps {
	layout: 'row' | 'column' | 'multi';
	name?: string;
	gap?: SpacingTypes;
}

interface ISelectableButtonGroupProps extends ISelectableGroupProps {
	children?: ReactNode;
	className?: string;
	error?: boolean;
	errorLabel?: string;
}

const SelectableGroupContainer = styled.div<ISelectableGroupProps>`
	display: flex;
	flex-direction: ${({ layout }) => (layout === 'row' ? 'row' : 'column')};
	flex-grow: 1;

	@media ${theme.responsive.breakpoints.tablet} {
		flex-direction: ${({ layout }) => (layout === 'column' ? 'column' : 'row')};
	}

	gap: ${({ layout, gap }) =>
		gap
			? theme.spacing[gap]
			: layout === 'column'
			? theme.spacing.lg
			: layout === 'row'
			? theme.spacing.sm
			: layout === 'multi'
			? theme.spacing.sm
			: 0};

	@media ${theme.responsive.breakpoints.tablet} {
		gap: ${theme.spacing.sm};
	}
`;

const SpacedText = spaced<IBasicTextProps>(BasicText);

const SelectableButtonGroup: FC<ISelectableButtonGroupProps> = ({
	children,
	className,
	error = false,
	errorLabel = '',
	layout,
	name,
	gap,
}) => {
	return (
		<>
			<SelectableGroupContainer
				className={className}
				role="radiogroup"
				layout={layout}
				aria-label={name}
				gap={gap}>
				{children}
			</SelectableGroupContainer>
			{error && exists(errorLabel) && (
				<SpacedText marginTop="sm" color="red" tint="darken40">
					{errorLabel}
				</SpacedText>
			)}
		</>
	);
};

export default styled(SelectableButtonGroup)``;

/** Selectable Button Group Types
 * row: buttons show only in a flex row
 * column: buttons show only in a flex column
 * multi: buttons show in a flex column on mobile, flex row on tablet and above
 */
